import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Percent, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import ProductCard from '@/components/ProductCard';
import { isOutOfStock } from '@/lib/inventoryUtils';

interface Product {
    id: string;
    name: string;
    description: string;
    slug: string;
    price_per_unit: number;
    original_price?: number;
    measurement_unit: string;
    total_inventory: number;
    min_quantity: number;
    max_quantity?: number;
    step_quantity: number;
    city: string;
    sale_type: string;
    sale_start?: string;
    sale_end?: string;
    images: string[];
    main_image_index?: number;
    package_coverage_per_unit?: number;
    minimum_packages?: number;
    grade?: 'A' | 'B' | 'C' | 'D';
    created_at?: string;
    custom_tags?: string[];
    group_buy_enabled?: boolean;
    liquidation_sale_enabled?: boolean;
    categories: {
        name: string;
    };
    subcategories: {
        name: string;
    };
}

const LiquidationProductsSection = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLiquidationProducts = async () => {
            try {
                const { data: allProducts, error: fetchError } = await supabase
                    .rpc('get_public_product_catalog', {
                        limit_count: 100,
                        offset_count: 0
                    });

                if (fetchError) {
                    console.error('Error fetching products:', fetchError);
                    return;
                }

                // Filter for liquidation products and exclude Out of Stock
                const liquidationProducts = (allProducts || []).filter((p: any) =>
                    p.liquidation_sale_enabled === true && !isOutOfStock(p)
                );

                if (liquidationProducts.length === 0) {
                    setLoading(false);
                    return;
                }

                // Fetch categories and subcategories data
                const categoryIds = liquidationProducts.map((p: any) => p.category_id).filter(Boolean);
                const subcategoryIds = liquidationProducts.map((p: any) => p.subcategory_id).filter(Boolean);

                const [categoriesResult, subcategoriesResult] = await Promise.all([
                    categoryIds.length > 0
                        ? supabase.from('categories').select('id, name').in('id', categoryIds)
                        : { data: [] },
                    subcategoryIds.length > 0
                        ? supabase.from('subcategories').select('id, name').in('id', subcategoryIds)
                        : { data: [] }
                ]);

                const categoriesMap = new Map((categoriesResult.data || []).map(c => [c.id, c]));
                const subcategoriesMap = new Map((subcategoriesResult.data || []).map(s => [s.id, s]));

                const transformedProducts = liquidationProducts.map((product: any) => ({
                    ...product,
                    categories: product.category_id ? categoriesMap.get(product.category_id) || { name: 'ללא קטגוריה' } : { name: 'ללא קטגוריה' },
                    subcategories: product.subcategory_id ? subcategoriesMap.get(product.subcategory_id) || { name: 'ללא תת-קטגוריה' } : { name: 'ללא תת-קטגוריה' }
                }));

                setProducts(transformedProducts.slice(0, 4));
            } catch (error) {
                console.error('Error fetching liquidation products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLiquidationProducts();
    }, []);

    if (!loading && products.length === 0) {
        return null;
    }

    if (loading) {
        return (
            <section className="py-16 bg-gradient-to-b from-background via-red-50/50 to-muted/30">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <Percent className="h-8 w-8 text-red-500" />
                            <h2 className="text-3xl font-bold">מכירות חיסול</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Card key={i} className="overflow-hidden animate-pulse">
                                <div className="h-64 bg-gray-200"></div>
                                <CardContent className="p-6">
                                    <div className="h-6 bg-gray-200 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-200 rounded mb-4"></div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-16 bg-gradient-to-b from-background via-red-50/50 to-muted/30">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Percent className="h-8 w-8 text-red-500" />
                        <h2 className="text-3xl md:text-4xl font-bold hebrew-heading">מכירות חיסול</h2>
                    </div>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        הזדמנות אחרונה! מוצרים במחירים מיוחדים עד גמר המלאי
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {products.map((product) => (
                        <ProductCard key={product.id} product={product} viewMode="grid" />
                    ))}
                </div>


            </div>
        </section>
    );
};

export default LiquidationProductsSection;
